import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, Suspense, lazy } from 'react';
import { Toaster } from '@/components/ui/sonner';
import { OfflineIndicator } from '@/components/OfflineIndicator';
import { MobileBottomNav } from '@/components/MobileBottomNav';
import { MobileOptimizations } from '@/components/MobileOptimizations';
import { ResponsiveProvider } from '@/components/ResponsiveContainer';
import ErrorBoundary from '@/components/ErrorBoundary';
import { RequireAuth, RequireRole } from '@/components/RequireAuth';
import '@/i18n'; // Initialize i18next
import LanguageGate from '@/components/LanguageGate';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { analytics } from '@/lib/analytics';
import { cache } from '@/lib/cache';
import '@/App.css';

// Eager load critical pages
import Home from '@/pages/Home';
import SearchResults from '@/pages/SearchResults';
import PropertyDetail from '@/pages/PropertyDetail';
import Login from '@/pages/Login';

// Lazy load non-critical pages for code splitting
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const CompareProperties = lazy(() => import('@/pages/CompareProperties'));
const MyAccount = lazy(() => import('@/pages/MyAccount'));
const SavedSearches = lazy(() => import('@/pages/SavedSearches'));
const Favorites = lazy(() => import('@/pages/Favorites'));
const HostDashboard = lazy(() => import('@/pages/HostDashboard'));
const GuestDashboard = lazy(() => import('@/pages/GuestDashboard'));
const CreateProperty = lazy(() => import('@/pages/CreateProperty'));
const EditProperty = lazy(() => import('@/pages/EditProperty'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const AdminEditProperty = lazy(() => import('@/pages/AdminEditProperty'));
const AdminDiagnostics = lazy(() => import('@/pages/AdminDiagnostics'));
const SubscriptionPlans = lazy(() => import('@/pages/SubscriptionPlans'));
const SubscriptionSuccess = lazy(() => import('@/pages/SubscriptionSuccess'));
const SubscriptionCancel = lazy(() => import('@/pages/SubscriptionCancel'));
const PropertyPlanSelection = lazy(() => import('@/pages/PropertyPlanSelection'));
const SubscriptionSuccessProperty = lazy(() => import('@/pages/SubscriptionSuccessProperty'));
const PropertySubscriptionManagement = lazy(() => import('@/pages/PropertySubscriptionManagement'));
const Terms = lazy(() => import('@/pages/Terms'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const About = lazy(() => import('@/pages/About'));
const FAQ = lazy(() => import('@/pages/FAQ'));
const HowItWorks = lazy(() => import('@/pages/HowItWorks'));
const Contact = lazy(() => import('@/pages/Contact'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Unauthorized = lazy(() => import('@/pages/Unauthorized'));

// App version - increment this when deploying new changes
const APP_VERSION = '2.3.0';

// Loading fallback component - R1-4: Translated
const PageLoader = () => {
  const language = localStorage.getItem('language') || 'pt';
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-[#00C2D1] border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-500 text-sm">{language === 'pt' ? 'Carregando...' : 'Loading...'}</p>
      </div>
    </div>
  );
};

function App() {
  // P2-17 Fix: Update document.title and html lang based on language
  useEffect(() => {
    const updateDocumentLanguage = () => {
      const language = localStorage.getItem('language') || 'pt';
      const isPtBR = language === 'pt';
      
      // Update html lang attribute
      document.documentElement.lang = isPtBR ? 'pt-BR' : 'en-US';
      
      // Update document title
      document.title = isPtBR 
        ? 'MagicStay Homes - Casas de Temporada em Orlando'
        : 'MagicStay Homes - Vacation Rentals in Orlando';
    };
    
    updateDocumentLanguage();
    
    // Listen for language changes
    const handleStorageChange = (e) => {
      if (e.key === 'language') {
        updateDocumentLanguage();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom language change event
    const handleLanguageChange = () => updateDocumentLanguage();
    window.addEventListener('languageChange', handleLanguageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    // REMOVE DARK MODE - Force light mode
    localStorage.removeItem('darkMode');
    document.documentElement.classList.remove('dark');
    
    // Clear expired cache on app load
    cache.clearExpired();
    
    // Track app initialization
    analytics.event('app_initialized', { version: APP_VERSION });
    
    // FORCE IMMEDIATE RELOAD if version changed
    const checkAndForceReload = () => {
      const storedVersion = localStorage.getItem('app_version');
      
      if (storedVersion && storedVersion !== APP_VERSION) {
        // Clear all caches
        if ('caches' in window) {
          caches.keys().then(names => {
            names.forEach(name => caches.delete(name));
          });
        }
        
        localStorage.setItem('app_version', APP_VERSION);
        window.location.reload(true);
        return;
      }
      
      if (!storedVersion) {
        localStorage.setItem('app_version', APP_VERSION);
      }
    };
    
    checkAndForceReload();
    
    const interval = setInterval(checkAndForceReload, 30 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Get language for ErrorBoundary
  const language = localStorage.getItem('language') || 'pt';

  return (
    <ErrorBoundary language={language}>
      <ResponsiveProvider>
        <LanguageProvider>
          <LanguageGate>
            <div className="App overflow-x-hidden">
              <MobileOptimizations />
              <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Critical pages - eager loaded */}
                  <Route path="/" element={<Home />} />
                  <Route path="/search" element={<SearchResults />} />
                  <Route path="/property/:id" element={<PropertyDetail />} />
                  <Route path="/login" element={<Login />} />
                  
                  {/* Auth pages - lazy loaded */}
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  
                  {/* User pages - require authentication */}
                  <Route path="/compare" element={<CompareProperties />} />
                  <Route path="/minha-conta" element={<RequireAuth><MyAccount /></RequireAuth>} />
                  <Route path="/saved-searches" element={<RequireAuth><SavedSearches /></RequireAuth>} />
                  <Route path="/favorites" element={<RequireAuth><Favorites /></RequireAuth>} />
                  <Route path="/dashboard" element={<RequireAuth><GuestDashboard /></RequireAuth>} />
                  
                  {/* Host pages - require authentication */}
                  <Route path="/anunciar" element={<SubscriptionPlans />} />
                  <Route path="/anunciar/plano/:propertyId" element={<RequireAuth><PropertyPlanSelection /></RequireAuth>} />
                  <Route path="/anunciar/sucesso" element={<RequireAuth><SubscriptionSuccessProperty /></RequireAuth>} />
                  <Route path="/plans" element={<Navigate to="/anunciar" replace />} />
                  <Route path="/subscription-plans" element={<Navigate to="/anunciar" replace />} />
                  <Route path="/subscription-success" element={<RequireAuth><SubscriptionSuccess /></RequireAuth>} />
                  <Route path="/subscription-cancel" element={<SubscriptionCancel />} />
                  <Route path="/host/choose-plan" element={<Navigate to="/anunciar" replace />} />
                  <Route path="/host/dashboard" element={<RequireAuth><HostDashboard /></RequireAuth>} />
                  <Route path="/host/create-property" element={<RequireAuth><CreateProperty /></RequireAuth>} />
                  <Route path="/host/edit-property/:propertyId" element={<RequireAuth><EditProperty /></RequireAuth>} />
                  <Route path="/host/imovel/:propertyId" element={<RequireAuth><PropertySubscriptionManagement /></RequireAuth>} />
                  <Route path="/host/imovel/:propertyId/assinatura" element={<RequireAuth><PropertySubscriptionManagement /></RequireAuth>} />
                  
                  {/* Static pages - lazy loaded */}
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/how-it-works" element={<HowItWorks />} />
                  <Route path="/contact" element={<Contact />} />
                  
                  {/* Admin - require admin role */}
                  <Route path="/admin" element={<RequireRole allowedRoles={['admin']}><AdminDashboard /></RequireRole>} />
                  <Route path="/admin/diagnostics" element={<RequireRole allowedRoles={['admin']}><AdminDiagnostics /></RequireRole>} />
                  <Route path="/admin/property/:propertyId/edit" element={<RequireRole allowedRoles={['admin']}><AdminEditProperty /></RequireRole>} />
                  
                  {/* Unauthorized - shown when user doesn't have permission */}
                  <Route path="/unauthorized" element={<Unauthorized />} />
                  
                  {/* 404 - Catch all unmatched routes */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <MobileBottomNav />
            </BrowserRouter>
            <Toaster richColors />
            <OfflineIndicator />
          </div>
        </LanguageGate>
      </LanguageProvider>
    </ResponsiveProvider>
  </ErrorBoundary>
  );
}

export default App;
